import { useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
  GeoJSON,
} from "react-leaflet";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { ProjectWithImpacts } from "../api/portfolio";
import { getTechColor, techTypeLabel } from "../../generators/utils";
import { formatImpact } from "../../../utils/format";
import { usePortfolioIntensityTiles } from "../hooks/usePortfolioIntensityTiles";
import {
  usePortfolioAggregate,
  type AggregationLevel,
  type AggregateFeatureProperties,
} from "../hooks/usePortfolioAggregate";
import { CARTO_DARK_URL } from "../../../lib/basemap";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PortfolioMapProps {
  projects: ProjectWithImpacts[];
  selectedProjectId?: string | null;
  onProjectClick?: (projectId: string) => void;
  /** Admin-only override. Defaults to the current user's customer. */
  customerId?: string;
  /** Active filter[key]=value pairs. When non-empty, the choropleth is
   * computed on the fly from matching projects only. */
  filters?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Auto-fit bounds
// ---------------------------------------------------------------------------

function FitBounds({ projects }: { projects: ProjectWithImpacts[] }) {
  const map = useMap();

  useEffect(() => {
    const coords = projects
      .filter((p) => p.latitude != null && p.longitude != null)
      .map((p) => [p.latitude!, p.longitude!] as [number, number]);

    if (coords.length === 0) return;

    if (coords.length === 1) {
      map.setView(coords[0], 8);
    } else {
      map.fitBounds(L.latLngBounds(coords), { padding: [40, 40] });
    }
  }, [map, projects]);

  return null;
}

// ---------------------------------------------------------------------------
// BA boundaries layer (dimmed context)
// ---------------------------------------------------------------------------

function BABoundaries() {
  const map = useMap();

  useEffect(() => {
    let cancelled = false;
    fetch("/data/us_ba_boundaries.geojson")
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        L.geoJSON(data, {
          style: {
            fillColor: "#475569",
            fillOpacity: 0.06,
            color: "#475569",
            weight: 0.5,
          },
          interactive: false,
        }).addTo(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [map]);

  return null;
}

// ---------------------------------------------------------------------------
// Marker size from capacity
// ---------------------------------------------------------------------------

function markerRadius(capacityMw: number): number {
  return Math.max(6, Math.min(18, Math.sqrt(capacityMw) * 1.5));
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

function Legend({ projects }: { projects: ProjectWithImpacts[] }) {
  const techs = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of projects) {
      const label = techTypeLabel(p.tech_type_id);
      if (!seen.has(label)) {
        seen.set(label, getTechColor(label));
      }
    }
    return Array.from(seen.entries());
  }, [projects]);

  if (techs.length === 0) return null;

  return (
    <div className="absolute bottom-3 left-3 z-[1000] rounded-lg bg-surface-800/90 border border-border px-3 py-2">
      {techs.map(([label, color]) => (
        <div key={label} className="flex items-center gap-2 text-xs text-text-secondary">
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: color }}
          />
          {label}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Choropleth overlay — colors polygons by share_pct (Viridis-like)
// ---------------------------------------------------------------------------

function choroplethColor(share: number): string {
  // share_pct spans ~4 orders of magnitude on a real portfolio (max ~5%,
  // median ~0.005%), so a linear ramp collapses 99% of polygons to dark
  // purple. Use log10 between 0.001% and 5% to spread the palette across
  // the actual distribution.
  const LOG_MIN = -3; // log10(0.001%)
  const LOG_MAX = Math.log10(5); // ~0.7 (log10(5%))
  const logShare = share > 0 ? Math.log10(share) : LOG_MIN;
  const t = Math.min(1, Math.max(0, (logShare - LOG_MIN) / (LOG_MAX - LOG_MIN)));
  const stops: Array<[number, [number, number, number]]> = [
    [0.0, [68, 1, 84]],
    [0.25, [59, 82, 139]],
    [0.5, [33, 145, 140]],
    [0.75, [94, 201, 98]],
    [1.0, [253, 231, 37]],
  ];
  for (let i = 1; i < stops.length; i++) {
    if (t <= stops[i][0]) {
      const lo = stops[i - 1];
      const hi = stops[i];
      const f = (t - lo[0]) / (hi[0] - lo[0] || 1);
      const r = Math.round(lo[1][0] + f * (hi[1][0] - lo[1][0]));
      const g = Math.round(lo[1][1] + f * (hi[1][1] - lo[1][1]));
      const b = Math.round(lo[1][2] + f * (hi[1][2] - lo[1][2]));
      return `rgb(${r},${g},${b})`;
    }
  }
  return "rgb(253,231,37)";
}

export function ChoroplethLayer({
  fc,
  level,
}: {
  fc: import("../hooks/usePortfolioAggregate").AggregateFeatureCollection;
  level: AggregationLevel;
}) {
  return (
    <GeoJSON
      key={`${level}-${fc.features.length}`}
      data={fc as unknown as GeoJSON.FeatureCollection}
      style={(feat) => {
        const p = (feat?.properties || {}) as AggregateFeatureProperties;
        const share = p.share_pct ?? 0;
        return {
          fillColor: share > 0 ? choroplethColor(share) : "#1e293b",
          color: "#ffffff",
          weight: 0.5,
          fillOpacity: share > 0 ? 0.65 : 0.1,
          opacity: 0.7,
        };
      }}
      onEachFeature={(feat, layer) => {
        const p = (feat.properties || {}) as AggregateFeatureProperties;
        const label = p.name ?? p.id ?? "—";
        const share = p.share_pct ?? 0;
        const valueStr =
          share >= 0.1 ? `${share.toFixed(1)}%` : share > 0 ? `<0.1%` : "0%";
        const dalysStr =
          p.dalys != null && p.dalys !== 0
            ? `<b>DALYs avoided: ${formatImpact(p.dalys, "dalys")}</b><br/>`
            : "";
        layer.bindTooltip(
          `<div class="text-xs"><b>${label}</b><br/>` +
            dalysStr +
            `Share of portfolio impact: ${valueStr}<br/>` +
            (p.rank ? `Rank: #${p.rank}<br/>` : "") +
            "" +
            `</div>`,
          { sticky: true },
        );
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// County / state / metro lookup — free-text search across choropleth features
// ---------------------------------------------------------------------------

export function PolygonLookup({
  fc,
  level,
}: {
  fc: import("../hooks/usePortfolioAggregate").AggregateFeatureCollection;
  level: AggregationLevel;
}) {
  const map = useMap();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return fc.features
      .filter((f) => {
        const p = f.properties as AggregateFeatureProperties;
        const name = (p.name ?? p.id ?? "").toString().toLowerCase();
        return name.includes(q);
      })
      .sort((a, b) => {
        const ap = a.properties as AggregateFeatureProperties;
        const bp = b.properties as AggregateFeatureProperties;
        return (bp.share_pct ?? 0) - (ap.share_pct ?? 0);
      })
      .slice(0, 8);
  }, [fc, query]);

  const jumpTo = (feat: (typeof fc.features)[number]) => {
    const layer = L.geoJSON(feat as unknown as GeoJSON.Feature);
    const bounds = layer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 9 });
    }
    const p = feat.properties as AggregateFeatureProperties;
    const share = p.share_pct ?? 0;
    const shareStr =
      share >= 0.1 ? `${share.toFixed(1)}%` : share > 0 ? `<0.1%` : "0%";
    const dalysStr =
      p.dalys != null && p.dalys !== 0
        ? `<b>DALYs avoided: ${formatImpact(p.dalys, "dalys")}</b><br/>`
        : "";
    L.popup({ closeButton: true, maxWidth: 260 })
      .setLatLng(bounds.getCenter())
      .setContent(
        `<div class="text-xs"><b>${p.name ?? p.id ?? "—"}</b><br/>` +
          dalysStr +
          `Share of portfolio impact: ${shareStr}<br/>` +
          (p.rank ? `Rank: #${p.rank}<br/>` : "") +
          `</div>`,
      )
      .openOn(map);
    setQuery("");
    setFocused(false);
  };

  const placeholder =
    level === "county"
      ? "Find county (e.g. Harris)"
      : level === "cbsa"
        ? "Find metro (e.g. Houston)"
        : "Find state";

  return (
    <div className="absolute top-10 left-2 z-[500] w-64">
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        className="w-full px-2.5 py-1.5 rounded-md bg-surface-700/95 border border-border text-[12px] text-text-primary placeholder:text-text-muted outline-none focus:border-quantum-500 backdrop-blur-sm"
      />
      {focused && matches.length > 0 && (
        <ul className="mt-1 rounded-md bg-surface-700/95 border border-border backdrop-blur-sm max-h-64 overflow-y-auto text-[11px]">
          {matches.map((f, i) => {
            const p = f.properties as AggregateFeatureProperties;
            const name = p.name ?? p.id ?? "—";
            const share = p.share_pct ?? 0;
            const dalys = p.dalys ?? null;
            return (
              <li key={p.id ?? i}>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    jumpTo(f);
                  }}
                  className="w-full flex justify-between items-center gap-2 px-2 py-1 hover:bg-surface-600 text-left"
                >
                  <span className="truncate text-text-primary">{name}</span>
                  <span className="tabular-nums text-text-muted">
                    {dalys != null && dalys !== 0
                      ? `${formatImpact(dalys, "dalys")} DALYs`
                      : share >= 0.1 ? `${share.toFixed(1)}%` : share > 0 ? "<0.1%" : "0%"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function ChoroplethStats({
  fc,
  level,
}: {
  fc: import("../hooks/usePortfolioAggregate").AggregateFeatureCollection;
  level: AggregationLevel;
}) {
  const top = useMemo(
    () =>
      [...fc.features]
        .filter((f) => (f.properties.share_pct ?? 0) > 0)
        .slice(0, 10),
    [fc],
  );
  const levelLabel =
    level === "cbsa" ? "metros" : level === "county" ? "counties" : "states";
  const totalDalys = fc.metadata?.total_dalys;
  const haveDalys = totalDalys != null && totalDalys !== 0;
  return (
    <div className="absolute top-2 right-2 z-[500] rounded-md bg-surface-700/95 border border-border px-3 py-2 text-[11px] text-text-secondary leading-tight backdrop-blur-sm max-w-[300px]">
      <div className="uppercase tracking-wider text-text-muted mb-1">
        Top {Math.min(top.length, 10)} {levelLabel} by PM2.5-attributable DALYs
      </div>
      {haveDalys && (
        <div className="text-text-muted text-[10px] mb-1">
          Portfolio total: <span className="text-text-primary tabular-nums">{formatImpact(totalDalys!, "dalys")} DALYs avoided</span>
        </div>
      )}
      {fc.metadata?.nonzero_count != null && (
        <div className="text-text-muted text-[10px] mb-1.5">
          {fc.metadata.nonzero_count}/{fc.metadata.feature_count} {levelLabel} receive any impact
        </div>
      )}
      <ol className="space-y-0.5">
        {top.map((f, i) => {
          const p = f.properties as AggregateFeatureProperties;
          const name = p.name ?? p.id ?? "—";
          const share = p.share_pct ?? 0;
          const dalys = p.dalys ?? null;
          return (
            <li key={p.id ?? i} className="flex justify-between gap-2">
              <span className="truncate text-text-primary">{name}</span>
              <span className="tabular-nums text-text-secondary">
                {dalys != null && dalys !== 0
                  ? `${formatImpact(dalys, "dalys")}`
                  : `${share.toFixed(1)}%`}
              </span>
            </li>
          );
        })}
      </ol>
      <div className="mt-2 pt-1.5 border-t border-border text-[10px] text-text-muted">
        {haveDalys
          ? "PM2.5-attributable DALYs allocated by population-weighted intake per polygon."
          : "Population-weighted PM2.5 intake summed in each polygon, normalised to % of portfolio total."}
      </div>
    </div>
  );
}

export function PortfolioMap({ projects, selectedProjectId, onProjectClick, customerId, filters }: PortfolioMapProps) {
  const navigate = useNavigate();

  const mappable = useMemo(
    () => projects.filter((p) => p.latitude != null && p.longitude != null),
    [projects],
  );

  // View mode: markers | heatmap | state | county | cbsa
  type ViewMode = "markers" | "heatmap" | AggregationLevel;
  const [viewMode, setViewMode] = useState<ViewMode>("markers");

  const showHeatmap = viewMode === "heatmap";
  const { data: intensityTiles } = usePortfolioIntensityTiles(customerId, showHeatmap);
  const heatmapReady = Boolean(intensityTiles);
  const effectiveShowHeatmap = showHeatmap && heatmapReady;

  const aggregationLevel: AggregationLevel | null =
    viewMode === "state" || viewMode === "county" || viewMode === "cbsa"
      ? viewMode
      : null;
  const { data: aggregate } = usePortfolioAggregate(
    aggregationLevel, customerId, aggregationLevel !== null, filters,
  );

  if (mappable.length === 0) return null;

  // Center fallback (will be overridden by FitBounds)
  const center: [number, number] = [39, -98];

  return (
    <div className="relative w-full h-full">
      <MapContainer
        center={center}
        zoom={4}
        zoomControl={false}
        style={{ width: "100%", height: "100%", background: "#0A1628" }}
      >
        <TileLayer
          url={CARTO_DARK_URL}
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
        />
        <BABoundaries />
        <FitBounds projects={mappable} />
        {effectiveShowHeatmap && intensityTiles && (
          <TileLayer
            url={intensityTiles.tile_url_template}
            minZoom={intensityTiles.min_zoom}
            maxZoom={intensityTiles.max_zoom}
            tileSize={intensityTiles.tile_size || 256}
            opacity={0.78}
            zIndex={350}
            attribution={
              intensityTiles.citation ||
              "InMAP ISRM v1.2.1 (Tessum, Hill & Marshall 2017)"
            }
          />
        )}

        {aggregationLevel && aggregate && (
          <>
            <ChoroplethLayer fc={aggregate} level={aggregationLevel} />
            <PolygonLookup fc={aggregate} level={aggregationLevel} />
          </>
        )}

        {mappable.map((p) => {
          const label = techTypeLabel(p.tech_type_id);
          const color = getTechColor(label);
          const isSelected = p.project_id === selectedProjectId;
          const isExisting = p.existing_project_flag;
          // In choropleth modes the filled tech-colored dots wash out the
          // polygon colors. Render smaller white-outlined pins as a location
          // reference instead.
          const inChoropleth = !!aggregationLevel;

          return (
            <CircleMarker
              key={p.project_id}
              center={[p.latitude!, p.longitude!]}
              radius={inChoropleth ? 3 : markerRadius(p.nameplate_capacity)}
              pathOptions={
                inChoropleth
                  ? {
                      color: isSelected ? "#00e5ff" : "#ffffff",
                      weight: isSelected ? 2 : 1,
                      fillColor: "#ffffff",
                      fillOpacity: 0.85,
                    }
                  : {
                      color: isSelected ? "#00e5ff" : color,
                      weight: isSelected ? 3 : 1.5,
                      fillColor: color,
                      fillOpacity: isExisting ? 0.85 : 0.6,
                      dashArray: isExisting ? undefined : "4 4",
                    }
              }
              eventHandlers={{
                click: () => onProjectClick?.(p.project_id),
              }}
            >
              <Popup>
                <div className="text-sm space-y-1 min-w-[180px]">
                  <div className="font-semibold text-gray-100">{p.project_name}</div>
                  <div className="text-gray-400">
                    {label} &middot; {Math.round(p.nameplate_capacity)} MW
                  </div>
                  {p.eia_ba_code && (
                    <div className="text-gray-400">BA: {p.eia_ba_code}</div>
                  )}
                  {p.dalys != null && (
                    <div className="text-gray-400">
                      DALYs: {formatImpact(p.dalys, "dalys")}
                      {p.usd != null && ` · USD: ${formatImpact(p.usd, "usd")}`}
                    </div>
                  )}
                  <button
                    className="text-cyan-400 hover:text-cyan-300 text-xs mt-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/app/projects/${encodeURIComponent(p.project_id)}?name=${encodeURIComponent(p.project_name)}`);
                    }}
                  >
                    View Details &rarr;
                  </button>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
      <Legend projects={mappable} />

      {/* View-mode dropdown — Markers / Heatmap / By state|county|metro */}
      <div className="absolute top-2 left-2 z-[500] bg-surface-700/90 border border-border rounded-md text-[11px] backdrop-blur-sm">
        <div className="flex items-center gap-1 px-1">
          <span className="text-text-muted uppercase tracking-wider px-1">View</span>
          {([
            { k: "markers", label: "Markers" },
            { k: "heatmap", label: "Heatmap" },
            { k: "state", label: "By state" },
            { k: "county", label: "By county" },
            { k: "cbsa", label: "By metro" },
          ] as const).map((opt) => {
            const isActive = viewMode === opt.k;
            const title =
              opt.k === "heatmap" && !heatmapReady && showHeatmap
                ? "No portfolio aggregate heatmap built yet"
                : undefined;
            return (
              <button
                key={opt.k}
                onClick={() => setViewMode(opt.k)}
                title={title}
                className={`px-2 py-1 rounded transition-colors ${
                  isActive
                    ? "bg-quantum-500/30 text-quantum-400"
                    : "text-text-secondary hover:text-text-primary"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {aggregationLevel && aggregate && (
        <ChoroplethStats fc={aggregate} level={aggregationLevel} />
      )}

      {effectiveShowHeatmap && intensityTiles && (
        <div
          className="absolute top-2 right-2 z-[500] rounded-md bg-surface-700/90 border border-border px-2.5 py-2 text-[10px] text-text-secondary leading-tight backdrop-blur-sm"
          style={{ pointerEvents: "none" }}
        >
          <div className="uppercase tracking-wider text-text-muted mb-1">
            Portfolio PM2.5 intake
          </div>
          <div
            className="h-1.5 rounded-full"
            style={{
              width: 140,
              background:
                "linear-gradient(to right, rgb(68,1,84), rgb(59,82,139), rgb(33,145,140), rgb(94,201,98), rgb(253,231,37))",
            }}
          />
          <div className="flex justify-between" style={{ width: 140 }}>
            <span>low</span>
            <span>hotspot</span>
          </div>
          <div className="mt-1 text-text-muted">
            Σ across {intensityTiles.n_projects ?? intensityTiles.generator_count ?? mappable.length} projects
          </div>
        </div>
      )}
    </div>
  );
}
